import { HttpErrorResponse } from "@angular/common/http";
import {
  Component,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { NgbNavChangeEvent } from "@ng-bootstrap/ng-bootstrap";
import { StateService, UIRouterGlobals } from "@uirouter/core";
import { finalize } from "rxjs/operators";
import { ChartHelperService } from "../chart/chart-helper.service";
import { TicksAgoService } from "../chart/ticks-ago.service";
import { DefaultChartResolutionService } from "../default-chart-resolution/default-chart-resolution.service";
import { EventService } from "../event-service/event.service";
import { KeyboardService } from "../keyboard/keyboard.service";
import { DeployService } from "../models/deploy.service";
import { GroupService } from "../models/group.service";
import { StatsmanService } from "../models/statsman.service";
import { RememberService } from "../remember/remember.service";

@Component({
  selector: "ab-notice-view",
  templateUrl: "./notice-view.component.html",
})
export class NoticeViewComponent implements OnInit, OnDestroy {
  @HostBinding("class") classAttribute =
    "d-md-flex flex-md-column flex-md-grow-1";
  @Input() public activeFilters: any;
  @Input() public filters: any;
  @Input() public group: any;
  @Input() public notice: any;
  @Input() public noticeQuery: any;
  @Input() public project: any;
  @Input() public user: any;

  public loading: boolean;
  public dur: string;
  public lastN: any;
  public chart: any;
  public tabGroups: any;
  public deletingGroup: boolean;
  public noticeCount: number;
  public deployRevision: string;
  public groupDeployAvailable: boolean;
  public routeStats: {
    requestCount: any;
    noticeCount: number;
    errorRate: number;
  };
  public activeTabIndex: string;

  private REMEMBER_CHART_DURATION_KEY = "noticeView.chartDuration";
  private REMEMBER_CHART_LAST_N_KEY = "noticeView.chartLastN";
  private unregisterKeydown: any;
  private groupResolvedSub: any;
  private groupMutedSub: any;
  private toggleOccurrenceListSub: any;
  private statsmanParams: any;

  constructor(
    private $state: StateService,
    private $uiRouterGlobals: UIRouterGlobals,
    private deployService: DeployService,
    private eventService: EventService,
    private groupService: GroupService,
    private keyboardService: KeyboardService,
    private rememberService: RememberService,
    private statsmanService: StatsmanService,
    private ticksAgoService: TicksAgoService,
    private chartHelperService: ChartHelperService,
    private defaultChartResolutionService: DefaultChartResolutionService,
  ) {}

  public loadChart(params: any) {
    if (params == null) {
      params = {};
    }
    this.loading = true;
    if (params.dur) {
      this.dur = params.dur;
      this.rememberService.set(this.REMEMBER_CHART_DURATION_KEY, params.dur);
    }
    if (params.last_n) {
      this.lastN = params.last_n;
      this.rememberService.set(this.REMEMBER_CHART_LAST_N_KEY, params.last_n);
    }

    if (this.statsmanParams == null) {
      this.statsmanParams = params;
    } else {
      this.statsmanParams = Object.assign(this.statsmanParams, params);
    }
    return this.statsmanService
      .queryGroupNoticesCount(this.statsmanParams)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe((data: any) => (this.chart = this.buildBarChartData(data)));
  }

  public resolveGroup(group: any) {
    return this.groupService.resolve(group).subscribe(() => {
      this.createUndoAction(group);
    });
  }

  public unresolveGroup(group: any) {
    return this.groupService.unresolve(group).subscribe(() => {
      this.createUndoAction(group);
    });
  }

  public muteGroup(group: any) {
    return this.groupService.mute(group).subscribe();
  }

  public unmuteGroup(group: any) {
    return this.groupService.unmute(group).subscribe();
  }

  public toggleOccurrenceList() {
    this.tabGroups[1][0].active = !this.tabGroups[1][0].active;
    this.tabGroups[1][1].active = !this.tabGroups[1][1].active;
    const active = this.tabGroups[1].filter((tabGroup: any) => {
      return tabGroup.active;
    });
    this.$state.go(this.$uiRouterGlobals.current.name, {
      tab: active[0].route,
    });
  }

  public deleteGroup() {
    if (!confirm("Delete this error? You cannot undo this action.")) {
      return;
    }

    this.deletingGroup = true;
    return this.groupService
      .delete(this.group)
      .pipe(
        finalize(() => {
          this.deletingGroup = false;
        }),
      )
      .subscribe(() => {
        this.eventService.publish("groups:deleteActiveGroup");
      });
  }

  public ngOnDestroy() {
    this.unregisterKeydown();
    this.groupResolvedSub.unsubscribe();
    this.groupMutedSub.unsubscribe();
    this.toggleOccurrenceListSub.unsubscribe();
  }

  public onNavChange($event: NgbNavChangeEvent) {
    const tabIndex = Number($event.nextId.split("-")[2]);
    const tab = this.tabGroups[tabIndex][0].route.split("@")[0];
    this.$state.go(this.$uiRouterGlobals.current.name, { tab });
    this.setTabGroups(tab);
  }

  public ngOnInit() {
    this.noticeCount = this.noticeQuery.count;
    this.notice = this.notice || this.noticeQuery.notices[0];

    if (this.notice.deployId > 0) {
      this.deployService
        .get({
          projectId: this.notice.projectId,
          deployId: this.notice.deployId,
        })
        .subscribe((deploy: any) => (this.deployRevision = deploy.revision));
    }

    if (this.group.deployId && this.group.deployId !== "0") {
      this.deployService
        .get({
          projectId: this.project.id,
          deployId: this.group.deployId,
        })
        .subscribe(
          (deploy: any) => {
            if (deploy) {
              this.groupDeployAvailable = true;
            }
          },
          (response: HttpErrorResponse) => {
            if (response.status === 404) {
              this.groupDeployAvailable = false;
            }
          },
        );
    }

    this.loadChart({
      projectId: this.group.projectId,
      groupId: this.group.id,
      ...this.defaultChartResolution(),
    });

    this.setTabGroups();

    this.activeTabIndex = `ngb-tab-${this.findActiveTabIndex()}`;

    // Handle keyboard navigation with keydown events rather than keypress events.
    // This is because arrow keys do not fire keypress events, and it's nice to use
    // a single event type for all keyboard nav.
    // NB: Uses the keyboard service to allow us to capture keyboard events when
    // the user/browser focus is outside the group list.
    this.unregisterKeydown = this.keyboardService.onKeydown(
      (event: KeyboardEvent) => {
        // Don't trigger on CMD+/CTRL+ key combinations e.g. CMD+R to reload.
        // TODO: move this check to the keyboard service?
        if (event.metaKey || event.ctrlKey) {
          return;
        }

        switch (event.code) {
          // toggle resolved state of group.
          case "KeyR":
            // Shift+R is used in groupsCtrl to toggle resolved state of selection.
            if (event.shiftKey) {
              return;
            }
            if (this.group.resolved) {
              this.unresolveGroup(this.group);
            } else {
              this.resolveGroup(this.group);
            }
            break;
          // toggle muted state of group.
          case "KeyM":
            // shift+m is used in abGroupList to toggle muted state of selection
            if (event.shiftKey) {
              return;
            }
            if (this.group.muted) {
              this.unmuteGroup(this.group);
            } else {
              this.muteGroup(this.group);
            }
            break;
          default:
            return;
        }
        return event.preventDefault();
      },
    );

    this.groupResolvedSub = this.eventService.subscribe(
      "group:resolved",
      (group: any) => {
        if (this.group.id === group.id) {
          this.group.resolved = group.resolved;
        }
      },
    );

    this.groupMutedSub = this.eventService.subscribe(
      "group:muted",
      (group: any) => {
        if (this.group.id !== group.id) {
          return;
        }
        return (this.group.muted = group.muted);
      },
    );

    this.toggleOccurrenceListSub = this.eventService.subscribe(
      "notice-view:toggleOccurrenceList",
      this.toggleOccurrenceList.bind(this),
    );
  }

  private buildBarChartData(statsmanData: any) {
    return {
      data: {
        labels: this.ticksAgoService.ticksFor(this.dur, this.lastN),
        datasets: [
          {
            label: "Errors",
            backgroundColor: "#b8b8b8", // ab-grey-700
            borderColor: "#b8b8b8", // ab-grey-700
            data: statsmanData.count,
          },
        ],
      },
      options: this.chartHelperService.defaultChartOptions(),
    };
  }

  private createUndoAction(group: any) {
    let undoAction;
    if (group.resolved) {
      undoAction = {
        message: "Error was resolved.",
        undoObservable: this.groupService.unresolve(group),
      };
    } else {
      undoAction = {
        message: "Error was unresolved.",
        undoObservable: this.groupService.resolve(group),
      };
    }
    this.eventService.publish("undo:registerAction", undoAction);
  }

  private findActiveTabIndex() {
    const visibleTabGroups = this.tabGroups.filter((tabGroup: any[]) => {
      return tabGroup.some((tab: any) => tab.isVisible);
    });

    for (const tabGroup of visibleTabGroups) {
      if (tabGroup.some((tab: any) => tab.active)) {
        return this.tabGroups.indexOf(tabGroup);
      }
    }

    return 0;
  }

  private setTabGroups(currentTab = this.$uiRouterGlobals.params.tab) {
    const tabRoutes = [
      "overview",
      "notice-detail",
      "notice-list",
      "aggregations",
      "comments",
    ];
    if (tabRoutes.indexOf(currentTab) < 0) {
      currentTab = tabRoutes[0];
    }

    this.tabGroups = [
      [
        {
          title: "Overview",
          route: tabRoutes[0],
          isVisible: true,
          active: currentTab === "overview",
        },
      ],
      [
        {
          title: "Occurrences",
          route: tabRoutes[1],
          isVisible: true,
          active: ["notice-detail", "occurrences"].indexOf(currentTab) > -1,
        },
        {
          title: "Occurrences",
          route: tabRoutes[2],
          isVisible: true,
          active: ["notice-list", "occurrence_list"].indexOf(currentTab) > -1,
        },
      ],
      [
        {
          title: "Aggregations",
          route: tabRoutes[3],
          isVisible: this.noticeCount > 1,
          active: currentTab === "aggregations",
        },
      ],
      [
        {
          title: "Comments",
          route: tabRoutes[4],
          isVisible: true,
          active: currentTab === "comments",
        },
      ],
    ];

    if (currentTab === "notice-list") {
      this.tabGroups[1][0].active = false;
      this.tabGroups[1][1].active = true;
    }
  }

  private defaultChartResolution(): { dur: string; last_n: string } {
    const dur =
      this.$uiRouterGlobals.params.dur ||
      this.rememberService.get(this.REMEMBER_CHART_DURATION_KEY);
    const lastN =
      this.$uiRouterGlobals.params.last_n ||
      this.rememberService.get(this.REMEMBER_CHART_LAST_N_KEY);
    if (dur && lastN) {
      return { dur, last_n: lastN };
    }

    if (this.project.firstNoticeReceivedAt == null) {
      // If firstNoticeReceivedAt is not set, this is likely a new project.
      return { dur: "90h/1h", last_n: "24" };
    } else {
      return this.defaultChartResolutionService.forTimestamp(
        this.project.firstNoticeReceivedAt,
      );
    }
  }
}
